// Patient-cohort PDF exporter — one entry point per cohort row on the
// Patient Search block. Renders a compact ledger in the same visual
// language as the ODB / NIHB payment PDFs so the auditor gets one
// consistent look across every downloadable report from the app.
//
// Callers pass:
//   - cohortName:  visible title ("Lost patients")
//   - rows:        cohort-specific objects (see column definitions)
//   - columns:     [{ header, accessor, halign?, width? }]
//   - subtitle:    small under-title context (viewMode label, scope)
//   - rangeStart / rangeEnd:  ISO strings — visible-range badge
//
// No math is done here — the caller has already assembled `rows` in
// the order they want printed. This module ONLY paints them.

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Short month set aligned with dashboard-utils.js so labels match
// exactly what the user sees on-screen.
const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function fmtIsoLong(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
  if (!y || !m || !d) return iso;
  return `${MONTH_SHORT[m - 1]} ${d}, ${y}`;
}

function fmtRangeLabel(startIso, endIso) {
  if (!startIso || !endIso) return "";
  const [ys, ms, ds] = startIso.split("-").map((s) => parseInt(s, 10));
  const [ye, me, de] = endIso.split("-").map((s) => parseInt(s, 10));
  if (ys === ye && ms === me) {
    return `${MONTH_SHORT[ms - 1]} ${ds}-${de}, ${ys}`;
  }
  if (ys === ye) {
    return `${MONTH_SHORT[ms - 1]} ${ds} - ${MONTH_SHORT[me - 1]} ${de}, ${ys}`;
  }
  return `${MONTH_SHORT[ms - 1]} ${ds}, ${ys} - ${MONTH_SHORT[me - 1]} ${de}, ${ye}`;
}

function drawHeader(doc, title, subtitle, periodLabel) {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor("#1B3B2E");
  doc.text(title, 14, 20);

  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor("#5C746A");
    doc.text(subtitle, 14, 28);
  }

  if (periodLabel) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor("#1B3B2E");
    doc.text(`Period: ${periodLabel}`, 14, 36);
  }

  const generatedAt = new Date().toISOString().replace("T", " ").slice(0, 16);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor("#5C746A");
  doc.text(`Generated: ${generatedAt} UTC`, pageWidth - 14, 20, {
    align: "right",
  });
}

export function exportPatientCohortPdf({
  cohortName,
  subtitle = "",
  rows,
  columns,
  rangeStart = null,
  rangeEnd = null,
  filename,
}) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const periodLabel = fmtRangeLabel(rangeStart, rangeEnd);

  drawHeader(doc, cohortName, subtitle, periodLabel);

  const head = [columns.map((c) => c.header)];
  const body = (rows || []).map((r) =>
    columns.map((c) => {
      const raw = typeof c.accessor === "function" ? c.accessor(r) : r[c.accessor];
      if (raw == null) return "—";
      if (c.format === "date") return fmtIsoLong(raw);
      return String(raw);
    }),
  );

  // Column styles come from the caller — we honour halign + width
  // per column so each cohort can shape its own table.
  const columnStyles = {};
  columns.forEach((c, i) => {
    columnStyles[i] = {};
    if (c.halign) columnStyles[i].halign = c.halign;
    if (c.width) columnStyles[i].cellWidth = c.width;
  });

  autoTable(doc, {
    head,
    body,
    startY: 44,
    theme: "striped",
    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: { top: 1.6, right: 2.4, bottom: 1.6, left: 2.4 },
      textColor: [27, 59, 46],
      lineColor: [230, 235, 232],
    },
    headStyles: {
      fillColor: [27, 59, 46],
      textColor: [247, 242, 231],
      fontStyle: "bold",
      halign: "left",
    },
    alternateRowStyles: { fillColor: [247, 249, 248] },
    columnStyles,
    didParseCell: (data) => {
      // Header cells inherit halign from the column config so a
      // right-aligned number column doesn't have a left-aligned header.
      const col = columns[data.column.index];
      if (data.section === "head" && col?.halign) {
        data.cell.styles.halign = col.halign;
      }
    },
  });

  // Footer TOTAL row directly under the table. Show both the number
  // of rendered rows AND the count of unique patients since row-count
  // can exceed patient-count when a cohort renders per-date breakdowns.
  const lastY = doc.lastAutoTable.finalY + 6;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor("#1B3B2E");
  const n = rows?.length || 0;
  const uniquePatients = new Set((rows || []).map((r) => r.patient).filter(Boolean)).size;
  const totalLabel =
    uniquePatients > 0 && uniquePatients !== n
      ? `TOTAL · ${n} row${n === 1 ? "" : "s"} · ${uniquePatients} unique patient${uniquePatients === 1 ? "" : "s"}`
      : `TOTAL · ${n} ${n === 1 ? "patient" : "patients"}`;
  doc.text(totalLabel, 14, lastY);

  const safeName = (filename ||
    cohortName.toLowerCase().replace(/[^a-z0-9]+/g, "-")).replace(/^-+|-+$/g, "");
  doc.save(`${safeName}-${new Date().toISOString().slice(0, 10)}.pdf`);
}
